import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseSiteLayoutModule, DayjsModule } from '@lib/util';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { LinkDeliveryReceiptDialogStore, SearchResult } from './link-delivery-receipt-dialog.store';
import { MatCheckbox } from '@angular/material/checkbox';

export interface LinkDeliveryReceiptDialogData {
    supplierCompanyId?: string;
    supplierName?: string;
    supplierDisplayName?: string;
    // Simplified shape of already linked receipts for this invoice, used for the header suggestions
    alreadyLinkedReceipts: { receiptId: number; receiptNumber: string }[];
    // Set of delivery item IDs that are already linked to this invoice
    alreadyLinkedItemIds: Set<number>;
    // Existing relation for the current invoice item, if any
    currentRelation?: {
        deliveryReceiptId: number;
        deliveryItemPosition: number;
        deliveryItemId: number;
        deliveryReceiptNumber: string;
        supplierName?: string;
        supplierNumber?: string | null;
    };
    // Whether the current invoice item is empty (no existing data)
    isInvoiceItemEmpty?: boolean;
}

export interface LinkDeliveryReceiptDialogResult {
    remove?: boolean;
    relation?: {
        deliveryReceiptId: number;
        deliveryItemPosition: number;
        deliveryItemId: number;
        deliveryReceiptNumber: string;
    };
    item?: {
        id: number;
        name: string;
        note: string;
        articleNumberSupplier: string;
        quantity: string;
        orderUnit: string;
        contentUnit: string;
        numberOfContentUnitsPerOrderUnit: number;
        shortDescription: string;
    };
}

@Component({
    selector: 'app-link-delivery-receipt-dialog',
    standalone: true,
    imports: [
        CommonModule,
        BaseSiteLayoutModule,
        MatDialogModule,
        MatRadioModule,
        ReactiveFormsModule,
        FormsModule,
        TranslateModule,
        DayjsModule,
        MatCheckbox,
    ],
    templateUrl: './link-delivery-receipt-dialog.component.html',
    styleUrls: ['./link-delivery-receipt-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LinkDeliveryReceiptDialogStore],
})
export class LinkDeliveryReceiptDialogComponent {
    private readonly dialogRef = inject(
        MatDialogRef<LinkDeliveryReceiptDialogComponent, LinkDeliveryReceiptDialogResult>,
    );
    readonly data = inject<LinkDeliveryReceiptDialogData>(MAT_DIALOG_DATA);
    readonly store = inject(LinkDeliveryReceiptDialogStore);

    // Form controls
    readonly includeClosedControl = new FormControl(false);

    // Local UI helpers
    expanded: Record<number, boolean> = {};
    readonly supplierMissing = computed(() => !this.store.supplierId());

    // Helper to identify which search results are already linked
    readonly linkedReceiptIds = computed(() => {
        const linked = this.store.linkedSummaries();
        return new Set(linked.map((receipt) => receipt.id));
    });

    // Helper to identify which delivery items are already linked to the current invoice
    readonly linkedItemIds = computed(() => {
        return this.data.alreadyLinkedItemIds;
    });

    constructor() {
        this.store.init(this.data.supplierCompanyId ?? '');
        if (this.data.alreadyLinkedReceipts?.length) {
            this.store.setLinkedReceiptBasicInfo(this.data.alreadyLinkedReceipts);
        }
        if (this.data.currentRelation) {
            this.store.preselectCurrent(this.data.currentRelation);
        }

        // Initialize FormControl with store value and set up value change subscription
        this.includeClosedControl.setValue(this.store.includeClosed());
        this.includeClosedControl.valueChanges.subscribe((value) => {
            if (value !== null) {
                this.store.setIncludeClosed(value);
                this.store.searchReceipts(this.store.search());
            }
        });
    }

    // Proxy signal readers for template compatibility
    results = () => this.store.results();
    selectedReceiptId = () => this.store.selectedReceiptId();
    selectedReceiptNumber = () => this.store.selectedReceiptNumber();
    loadingItems = () => this.store.loadingItems();
    receiptItems = () => this.store.receiptItems();
    selectedItemId = () => this.store.selectedItemId();
    selectedOrderItem = () => this.store.selectedOrderItem();
    loadingOrderItem = () => this.store.loadingOrderItem();

    // Template proxy methods
    selectReceipt(r: SearchResult) {
        if (r.inApproval && !this.linkedReceiptIds().has(r.id)) {
            return;
        }
        this.store.selectReceipt(r);
    }

    // Helper to select from a linked summary row
    selectReceiptFromSummary(rel: { id: number; receiptNumber: string; supplierName: string }) {
        // Load full receipt details when selecting from linked summaries
        this.store.loadReceiptDetailsAndSelect(rel.id, rel.receiptNumber);
    }

    backToReceipts() {
        this.store.backToReceipts();
    }

    pickItem(item: { id: number; positionReceipt: number }) {
        // Don't allow selection of already linked items (unless it's the current relation being edited)
        if (this.isItemLinkedToInvoice(item.id) && !this.isCurrentRelation(item.id)) {
            return;
        }

        // Check if the item is already selected - if so, deselect it (toggle behavior)
        if (this.selectedItemId() === item.id) {
            this.store.deselectItem();
        } else {
            this.store.pickItem(item);
        }
    }

    // Check if an item is already linked to the current invoice
    isItemLinkedToInvoice(itemId: number): boolean {
        return this.linkedItemIds().has(itemId);
    }

    // Check if an item is the current relation being edited
    isCurrentRelation(itemId: number): boolean {
        return this.data.currentRelation?.deliveryItemId === itemId;
    }

    // Check if an item should be disabled (linked but not current relation)
    isItemDisabled(itemId: number): boolean {
        return this.isItemLinkedToInvoice(itemId) && !this.isCurrentRelation(itemId);
    }

    toggleExpand(itemId: number) {
        this.expanded[itemId] = !this.expanded[itemId];
    }

    confirm() {
        const rid = this.store.selectedReceiptId();
        const rnr = this.store.selectedReceiptNumber();
        const iid = this.store.selectedItemId();
        const pos = this.store.selectedItemPosition();
        const selectedItem = this.store.selectedItem();

        if (rid && iid && pos != null && selectedItem) {
            this.dialogRef.close({
                relation: {
                    deliveryReceiptId: rid,
                    deliveryItemId: iid,
                    deliveryItemPosition: pos,
                    deliveryReceiptNumber: rnr,
                },
                item: {
                    id: selectedItem.id,
                    name: selectedItem.name,
                    note: selectedItem.note,
                    articleNumberSupplier: selectedItem.articleNumberSupplier,
                    quantity: selectedItem.quantity,
                    orderUnit: selectedItem.orderUnit,
                    contentUnit: selectedItem.contentUnit,
                    numberOfContentUnitsPerOrderUnit: selectedItem.numberOfContentUnitsPerOrderUnit,
                    shortDescription: selectedItem.shortDescription,
                    orderItemId: selectedItem.orderItemId,
                },
            });
        }
    }

    cancel() {
        this.dialogRef.close();
    }
}
